import { ChangeDetectorRef, Component, OnInit, OnDestroy, AfterViewInit, ViewChild } from '@angular/core';
import { MessageService } from "primeng/api";
import { LeaseDetailService } from "@modules/private/lease/data/services/view/lease-detail.service";
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { LEASE_TABS_CONFIG, LeaseTabConfig } from '../data/constants/lease-detail-tabs.constant';
import { AssignWalletDialogComponent } from '@modules/private/lease/components/assign-wallet-dialog/assign-wallet-dialog.component';
import { isFolioModifiable } from '@modules/private/lease/data/utils/folio.util';
import { AuthService } from '@data/services/modules/auth/auth.service';

@Component({
  selector: 'app-lease-detail',
  templateUrl: './lease-detail.component.html',
  styleUrls: ['./lease-detail.component.scss'],
  providers: [MessageService]
})
export class LeaseDetailComponent implements OnInit, OnDestroy, AfterViewInit {
  activeTabKey: string = 'cliente';
  private subscriptions: Subscription[] = [];
  readonly tabsConfig = LEASE_TABS_CONFIG;
  @ViewChild('assignDialog') assignDialog!: AssignWalletDialogComponent;

  /** Estado del diálogo de edición del número del folio (= oldId). */
  editFolioVisible = false;
  editFolioLoading = false;

  constructor(
    public leaseDetailService: LeaseDetailService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private messageService: MessageService,
    private auth: AuthService,
  ) { }

  /**
   * Pestañas efectivamente RENDERIZADAS, en orden. Debe replicar EXACTAMENTE las
   * condiciones del template (`*ngIf` de status + `*appIfHasPermission`) para que el
   * índice de PrimeNG (que solo cuenta tabs renderizadas) mapee al `key` correcto.
   * Antes se indexaba contra el array estático completo y solo funcionaba porque los
   * tabs condicionales estaban al final; gatear Facturación (en medio) lo rompía.
   */
  get visibleTabs(): LeaseTabConfig[] {
    const status = this.leaseDetailService.data?.status;
    return this.tabsConfig.filter(tab => {
      if (tab.conditional && status === 'Pendiente') return false;
      if (tab.permission && !this.auth.userHasAuthorization(tab.permission[0], tab.permission[1])) return false;
      return true;
    });
  }

  /**
   * Fuente ÚNICA de visibilidad de cada `<p-tabPanel>`: el template lo consulta por
   * `key` en vez de duplicar las condiciones de status/permiso. Así el conjunto de
   * tabs renderizadas es SIEMPRE idéntico a `visibleTabs`, y el mapeo índice↔key no
   * se desalinea.
   */
  isTabVisible(key: string): boolean {
    return this.visibleTabs.some(tab => tab.key === key);
  }

  /** El folio es editable solo si calza con los patrones `R-#-...` o `R-...`. */
  isFolioEditable(): boolean {
    return isFolioModifiable(this.leaseDetailService.data?.folio);
  }

  openEditFolio(): void {
    if (this.isFolioEditable()) this.editFolioVisible = true;
  }

  /**
   * Persiste el nuevo folio + el `oldId` (= el número del folio). Recarga el detalle
   * (refresca el chip) y notifica el resultado.
   */
  saveFolio(payload: { folio: string; oldId: string }): void {
    this.editFolioLoading = true;
    this.leaseDetailService.updateFolio(payload.folio, payload.oldId).subscribe({
      next: (r: any) => {
        this.editFolioLoading = false;
        if (!r?.error) {
          this.editFolioVisible = false;
          this.leaseDetailService.getLeaseDetail();
          this.messageService.add({
            severity: 'success',
            summary: 'Folio actualizado',
            detail: 'El número del folio (oldId) se guardó correctamente.',
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: r?.msg || 'No se pudo actualizar el folio.',
          });
        }
      },
      error: () => {
        this.editFolioLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo actualizar el folio.',
        });
      },
    });
  }

  /**
   * Espejo del gate del botón de cartera. El chip de cartera se pinta dos veces —botón para quien
   * puede asignar, texto plano para quien no—, y `*appIfHasPermission` solo sabe mostrar; sin este
   * getter el `*ngIf` del texto plano no tendría con qué negar la misma condición y los dos
   * aparecerían juntos.
   */
  /** Puede tocar el folio. Mismo permiso que la cartera; se pregunta aparte porque se lee distinto. */
  get canEditFolio(): boolean {
    return this.auth.userHasAuthorization('leases', 'update');
  }

  get canAssignWallet(): boolean {
    return this.auth.userHasAuthorization('leases', 'update');
  }

  /** Abre el diálogo para asignar/cambiar la cartera del arrendamiento. */
  openAssignWallet(): void {
    this.assignDialog.open();
  }

  /** Recarga el detalle tras asignar la cartera. */
  onWalletAssigned(): void {
    this.leaseDetailService.getLeaseDetail();
  }

  ngOnInit(): void {
    // Suscribirse a cambios de ruta para recargar datos al navegar entre leases
    const paramSub = this.activatedRoute.paramMap.subscribe(params => {
      const newId = params.get('id') ?? '';
      if (newId && newId !== this.leaseDetailService.id) {
        this.leaseDetailService.id = newId;
        this.leaseDetailService.getLeaseDetail();
      } else if (newId && !this.leaseDetailService.data) {
        this.leaseDetailService.id = newId;
        this.leaseDetailService.getLeaseDetail();
      }
    });
    this.subscriptions.push(paramSub);

    // Carga inicial si paramMap no disparó (mismo ID)
    const initialId = this.activatedRoute.snapshot.paramMap.get('id') ?? '';
    if (initialId && !this.leaseDetailService.data) {
      this.leaseDetailService.id = initialId;
      this.leaseDetailService.getLeaseDetail();
    }

    const tabParam = this.activatedRoute.snapshot.queryParamMap.get('tab');
    this.activeTabKey = this.validateTabKey(tabParam);

    const queryParamsSub = this.activatedRoute.queryParams.subscribe(params => {
      const newTabKey = this.validateTabKey(params['tab']);
      if (newTabKey !== this.activeTabKey) {
        this.activeTabKey = newTabKey;
        this.cdr.detectChanges();
      }
    });
    this.subscriptions.push(queryParamsSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.leaseDetailService.resetServiceState();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.scrollToActiveTab(), 150);
  }

  getActiveTabIndex(): number {
    const index = this.visibleTabs.findIndex(tab => tab.key === this.activeTabKey);
    return index >= 0 ? index : 0;
  }

  onTabChange(event: any): void {
    const newTabKey = this.visibleTabs[event.index]?.key;
    if (newTabKey && newTabKey !== this.activeTabKey) {
      this.activeTabKey = newTabKey;
      this.updateUrlTab(newTabKey);
    }
  }

  private validateTabKey(tabKey: string | null): string {
    if (!tabKey) return 'cliente';
    //* Si el deep-link apunta a una pestaña que este rol no puede ver (o no aplica al
    //* status), cae a 'cliente' en vez de dejar un índice fuera de rango.
    const visible = this.visibleTabs.some(tab => tab.key === tabKey);
    if (visible) return tabKey;
    //* La pestaña «Pagos / Aplicaciones» la absorbió Exigibilidad (F7): un enlace guardado
    //* aterriza donde ahora vive esa información, no en Cliente.
    if (tabKey === 'pagos-aplicaciones' && this.visibleTabs.some(tab => tab.key === 'exigibilidad')) return 'exigibilidad';
    return 'cliente';
  }

  private scrollToActiveTab(): void {
    const activeIndex = this.getActiveTabIndex();
    const tabNavContainer = document.querySelector('.p-tabview-nav-content') as HTMLElement;
    const tabNav = document.querySelector('.p-tabview-nav') as HTMLElement;
    const tabHeaders = document.querySelectorAll('.p-tabview-nav li');
    
    if (!tabHeaders.length || !tabNavContainer) {
      setTimeout(() => this.scrollToActiveTab(), 300);
      return;
    }
    
    const activeTabHeader = tabHeaders[activeIndex] as HTMLElement;
    
    if (activeTabHeader && tabNavContainer) {
      const containerRect = tabNavContainer.getBoundingClientRect();
      const tabRect = activeTabHeader.getBoundingClientRect();
      const scrollLeft = tabRect.left - containerRect.left + tabNavContainer.scrollLeft - (containerRect.width / 2) + (tabRect.width / 2);
      
      tabNavContainer.scrollLeft = scrollLeft;
      
      if (tabNav && tabNavContainer.scrollLeft === 0) {
        tabNav.scrollLeft = scrollLeft;
      }
    }
  }

  private updateUrlTab(tabKey: string): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: { tab: tabKey },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }
}
